/**
 * @file partner.schema.ts
 * @description Define o schema Zod para a entidade 'Partner'.
 * Um parceiro representa uma organização B2B (cliente ou parceiro) que utiliza
 * a plataforma, podendo ter identidade visual própria (white labeling)
 * e acesso a funcionalidades exclusivas.
 */

import { z } from 'zod';
import { MaternityPhaseEnum, type MaternityPhase } from './maternity-phase.schema';
import { BarrierConfigSchema } from './barrier.schema';
import { RegraDeBloqueioSchema } from './bloqueio.schema';
// ButtonActionSchema mora em arquivo próprio porque também é usado pelos cards
// de preparo do agendamento — ver button-action.schema.ts.
import { ButtonActionSchema } from './button-action.schema';
import { TranscodingStatusEnum, VideoSubtypeEnum } from './content.schema';
import { PartnerSchedulingConfigSchema } from './scheduling-common.schema';
import { brandFeaturesField } from './brand-features';
import { ChatPolicySchema } from './chat-policy';
import { dominiosField } from './company.schema';

// O enum das fases da maternidade mora em `maternity-phase.schema.ts` — é
// vocabulário de domínio, não coisa de parceiro. Reexportado no fim deste
// arquivo para não quebrar quem já importava daqui.

// --- Helper Schemas & Enums ---

/**
 * Define as categorias de parceiros, com base na lista fornecida.
 * Usar um enum padroniza os dados para filtros e análises futuras.
 */
const PartnerType = z.enum([
  'BENEFIT_ADMINISTRATOR',   // Administradora de Benefícios
  'COLLECTIVE',              // Coletivo
  'CORPORATION',             // Corporação / Empresa
  'GOVERNMENT_AGENCY',       // Órgão do Governo
  'GROUP_MEDICINE',          // Medicina de Grupo
  'HEALTH_PLAN_OPERATOR',    // Operadora de Plano de Saúde
  'HOSPITAL',                // Hospital
  'INSURANCE_COMPANY',       // Seguradora
  'MEDICAL_CLINIC',          // Clínica Médica
  'MEDICAL_COOPERATIVE',     // Cooperativa Médica
  'PHARMACEUTICAL_INDUSTRY', // Indústria Farmacêutica
  'SELF_EMPLOYED',           // Autônomo
  'SELF_MANAGED_HEALTHCARE', // Autogestão
  'SOCIAL_ORGANIZATION',     // Organização Social
]);

/**
 * Define o status operacional de um parceiro.
 */
const PartnerStatus = z.enum(['active', 'inactive', 'onboarding']);

/**
 * Define a estrutura para o esquema de cores da identidade visual (white labeling).
 */
const ColorSchemeSchema = z.object({
  primary: z.string(),
  secondary: z.string(),
  background: z.string(),
  text: z.string(),
  /**
   * A cor de destaque (A094). Ainda sem uso no app: nasce para ser registrada
   * e aplicada depois. Vazia (`null` ou ausente) quer dizer "a primária" — e
   * fica vazia de propósito, em vez de copiar a primária, para acompanhá-la
   * se ela mudar. Quem lê usa `tokensFromBrand(...).accent`, que já resolve.
   */
  accent: z.string().nullable().optional(),
});

/**
 * Agrupa todas as configurações de identidade visual do parceiro.
 */
const BrandingSchema = z.object({
  /**
   * Opcional porque marca nova não tem logo: o upload só existe depois de
   * salvar (o form diz isso em voz alta — "salve a marca primeiro para enviar
   * o logo"). Era `.nullable()` sem `.optional()`, então omitir o campo
   * derrubava a criação com 500, e o formulário contradizia o próprio aviso.
   */
  logoUrl: z.string().url().or(z.literal('')).nullable().optional().default(null),
  logoHasOwnBackground: z.boolean().default(false),
  /**
   * Logo para fundo colorido (07/10/2026, A076): usado onde o fundo é a cor
   * da marca ou escuro — topo do cadastro, telas de código e conclusão,
   * cabeçalho dos e-mails, vídeo de boas-vindas. Sem ele, essas telas usam o
   * `logoUrl`. Opcional e anulável: `null` remove.
   */
  logoSobreCorUrl: z.string().url().or(z.literal('')).nullable().optional(),
  /**
   * Símbolo quadrado da marca (A076): ícone do app no celular e avatares
   * redondos. Sem ele, usa-se o `logoUrl` encaixado, sem esticar.
   */
  simboloUrl: z.string().url().or(z.literal('')).nullable().optional(),
  colors: ColorSchemeSchema,

  /**
   * URLs dos ícones PWA para instalação do app na home screen.
   * Gerados automaticamente a partir do logo no admin.
   */
  pwaIcons: z.object({
    icon192: z.string().url().nullable().optional(),
    icon512: z.string().url().nullable().optional(),
    iconMaskable192: z.string().url().nullable().optional(),
    iconMaskable512: z.string().url().nullable().optional(),
    iosIcon: z.string().url().nullable().optional(),
  }).optional(),
});

/**
 * Define a estrutura para um único contato dentro de um parceiro.
 */
const PartnerContactSchema = z.object({
  name: z.string(),
  portraitUrl: z.string().url().nullable(),
  position: z.string(),
  email: z.string().email().nullable(),
  phone: z.string().nullable(),
  whatsapp: z.boolean(),
});

// --- Content Preferences (com migração transparente de field names) ---

/**
 * Schema canônico de preferências de conteúdo — usa nomenclatura "Video"
 * em substituição à legada "Course".
 *
 * Renomeações aplicadas (com aliases legados aceitos via preprocess abaixo):
 *   hiddenEvaCourseIds        → hiddenEvaVideoIds
 *   unlockedEvaCourseIds      → unlockedEvaVideoIds
 *   importedCourses[].courseId→ importedVideos[].videoId
 *   importedCourses           → importedVideos
 *   hiddenImportedCourseIds   → hiddenImportedVideoIds
 *   unlockedImportedCourseIds → unlockedImportedVideoIds
 *   importedCoursesLocked     → importedVideosLocked
 *   lockedModuleIds           → lockedSectionIds
 */
/**
 * Curadoria de UM item recebido por concessão, seja qual for a origem (fase 8
 * do plano "A Eva deixa de ser especial"). Substitui as cinco listas abaixo
 * — `hiddenEvaVideoIds`, `unlockedEvaVideoIds`, `hiddenImportedVideoIds`,
 * `unlockedImportedVideoIds`, `magazineComments` —, que só existiam em
 * dobro porque "Eva" e "importado" eram a mesma coisa vista de duas origens.
 * As listas continuam sendo lidas (expand) por `curadoriaDoRecebido`; a
 * migração `2026-09-curadoria-do-acervo-recebido.md` copia e o contract
 * as retira.
 */
export const AnotacaoDeRevistaSchema = z.object({
  sectionTitle: z.string(),
  comment: z.string(),
  position: z.enum(['START', 'END']).default('START'),
});
export type AnotacaoDeRevista = z.infer<typeof AnotacaoDeRevistaSchema>;

export const CuradoriaDoItemSchema = z.object({
  /** A marca de destino esconde o item das pacientes dela. */
  oculto: z.boolean().optional(),
  /** Livre da barreira de conteúdo, mesmo com "recebidos travados por padrão". */
  destravado: z.boolean().optional(),
  /** Anotações que a marca de destino põe numa revista recebida. */
  anotacoes: z.array(AnotacaoDeRevistaSchema).optional(),
});
export type CuradoriaDoItem = z.infer<typeof CuradoriaDoItemSchema>;

const ContentPreferencesSchema = z.object({
  /** Curadoria por item recebido, indexada pelo id do conteúdo (fase 8). */
  curadoriaDoAcervoRecebido: z.record(z.string(), CuradoriaDoItemSchema).default({}),
  /**
   * Revista recebida nasce atrás da barreira de conteúdo? Generaliza
   * `evaMagazinesLocked` (que só falava da Eva); ausente, lê o legado.
   * Vídeo recebido é sempre travado por padrão — se destrava um a um.
   */
  revistasRecebidasTravadas: z.boolean().optional(),

  hiddenEvaVideoIds: z.array(z.string()).default([]),
  magazinesEnabled: z.boolean().default(true),
  magazineComments: z.array(z.object({
    magazineId: z.string(),
    sectionTitle: z.string(),
    comment: z.string(),
    position: z.enum(['START', 'END']).default('START'),
  })).default([]),
  gestationalMagazineDisplayMode: z.enum(['ALL_WEEKS', 'CURRENT_WEEK']).default('ALL_WEEKS'),
  enableContentExceptions: z.boolean().default(false),
  evaMagazinesLocked: z.boolean().default(true),
  unlockedEvaVideoIds: z.array(z.string()).default([]),

  // --- Vídeos Importados ---
  importedVideos: z.array(z.object({
    videoId: z.string(),
    sourcePartnerId: z.string(),
    shareCode: z.string(),
    visibility: z.enum(['VISIBLE', 'HIDDEN']).default('VISIBLE'),
    importedAt: z.coerce.date(),
  })).default([]),
  hiddenImportedVideoIds: z.array(z.string()).default([]),
  unlockedImportedVideoIds: z.array(z.string()).default([]),
  importedVideosLocked: z.boolean().default(true),

  // --- Bloqueio granular por seção (antigo módulo) ---
  lockedSectionIds: z.array(z.string()).default([]),

  // --- Trail-only mode ---
  // Quando ativo, usuários só veem vídeos vinculados a trilhas
  trailOnlyMode: z.boolean().default(false),
});

/**
 * Preprocessor que aceita partner docs ainda no formato legado (com nomes de
 * campo antigos contendo "Course"/"Module") e mapeia para o schema canônico.
 *
 * Durante a janela de migração, esta camada garante que código novo consegue
 * ler partner docs que ainda não foram migrados pelo script. Após a migração
 * estar verificada em todos os ambientes, este preprocess pode ser removido.
 */
const ContentPreferencesWithLegacyAliasesSchema = z.preprocess(
  (raw) => {
    if (typeof raw !== 'object' || raw === null) return raw;
    const v = raw as Record<string, unknown>;

    // Renomeia importedCourses[].courseId → importedVideos[].videoId
    let importedVideos = v.importedVideos as unknown;
    if (importedVideos === undefined && Array.isArray(v.importedCourses)) {
      importedVideos = (v.importedCourses as Array<Record<string, unknown>>).map((item) => ({
        ...item,
        videoId: item.videoId ?? item.courseId,
      }));
    }

    return {
      ...v,
      hiddenEvaVideoIds: v.hiddenEvaVideoIds ?? v.hiddenEvaCourseIds,
      unlockedEvaVideoIds: v.unlockedEvaVideoIds ?? v.unlockedEvaCourseIds,
      importedVideos,
      hiddenImportedVideoIds: v.hiddenImportedVideoIds ?? v.hiddenImportedCourseIds,
      unlockedImportedVideoIds: v.unlockedImportedVideoIds ?? v.unlockedImportedCourseIds,
      importedVideosLocked: v.importedVideosLocked ?? v.importedCoursesLocked,
      lockedSectionIds: v.lockedSectionIds ?? v.lockedModuleIds,
    };
  },
  ContentPreferencesSchema
);



/**
 * A curadoria de um item recebido, lendo o campo novo e caindo nas listas
 * legadas enquanto a migração não roda (expand). Item que a marca de
 * destino nunca tocou: `{}`.
 */
export function curadoriaDoRecebido(
  prefs: Partial<ContentPreferences> | null | undefined,
  contentId: string
): CuradoriaDoItem {
  const nova = prefs?.curadoriaDoAcervoRecebido?.[contentId];
  const oculto = nova?.oculto ?? ((prefs?.hiddenEvaVideoIds ?? []).includes(contentId) || (prefs?.hiddenImportedVideoIds ?? []).includes(contentId) || undefined);
  const destravado = nova?.destravado ?? ((prefs?.unlockedEvaVideoIds ?? []).includes(contentId) || (prefs?.unlockedImportedVideoIds ?? []).includes(contentId) || undefined);
  const legadas = (prefs?.magazineComments ?? []).filter((c) => c.magazineId === contentId).map(({ sectionTitle, comment, position }) => ({ sectionTitle, comment, position }));
  const anotacoes = nova?.anotacoes ?? (legadas.length ? legadas : undefined);
  return { ...(oculto ? { oculto } : {}), ...(destravado ? { destravado } : {}), ...(anotacoes ? { anotacoes } : {}) };
}

/** Revista recebida nasce atrás da barreira? Campo novo, senão o legado da Eva. */
/**
 * A lista branca vale para esta marca? Só com o interruptor ligado E a
 * funcionalidade `CONTENT_WHITELIST` (A107). É a pergunta que todo filtro de
 * conteúdo faz — nunca `contentPreferences.enableContentExceptions` sozinho:
 * marca sem a funcionalidade não tem lista branca, mesmo com o campo `true`.
 */
export function listaBrancaAtiva(
  marca: { enabledFeatures?: readonly string[] | null; contentPreferences?: { enableContentExceptions?: boolean | null } | null } | null | undefined
): boolean {
  return !!marca?.contentPreferences?.enableContentExceptions && (marca.enabledFeatures ?? []).includes('CONTENT_WHITELIST');
}

/**
 * As preferências de conteúdo como os filtros devem ler: iguais às gravadas,
 * com `enableContentExceptions` respondido por `listaBrancaAtiva`.
 */
export function preferenciasDeConteudoEfetivas<M extends { enabledFeatures?: readonly string[] | null; contentPreferences?: Partial<ContentPreferences> | null }>(
  marca: M | null | undefined
): Partial<ContentPreferences> | null {
  if (!marca?.contentPreferences) return null;
  return { ...marca.contentPreferences, enableContentExceptions: listaBrancaAtiva(marca) };
}

export function revistasRecebidasTravadas(prefs: Partial<ContentPreferences> | null | undefined): boolean {
  return prefs?.revistasRecebidasTravadas ?? prefs?.evaMagazinesLocked ?? true;
}

// --- Domínio personalizado da marca ---

export const DominioPersonalizadoStatusEnum = z.enum(['AGUARDANDO_DNS', 'ATIVO', 'ERRO']);
export type DominioPersonalizadoStatus = z.infer<typeof DominioPersonalizadoStatusEnum>;

/** Um registro que a marca precisa publicar no DNS dela, como o Hosting pede. */
export const RegistroDnsSchema = z.object({
  domainName: z.string(),
  type: z.string(),
  rdata: z.string(),
  /** `ADD` ou `REMOVE`, na linguagem do Hosting. */
  requiredAction: z.string().optional(),
  /**
   * O Hosting já encontrou este registro no DNS. Um `ADD` publicado continua
   * na lista enquanto o certificado é emitido (o TXT do desafio precisa
   * ficar); a tela o mostra como "publicado — mantenha", não como pendente.
   */
  publicado: z.boolean().optional(),
});
export type RegistroDns = z.infer<typeof RegistroDnsSchema>;

export const DominioPersonalizadoSchema = z.object({
  hostname: z.string(),
  status: DominioPersonalizadoStatusEnum.default('AGUARDANDO_DNS'),
  /** Estados crus do Hosting (`ownershipState`, `hostState`, `certState`), para o suporte ler. */
  ownershipState: z.string().optional(),
  hostState: z.string().optional(),
  certState: z.string().optional(),
  /** O que falta publicar no DNS da marca. Vazio quando tudo está no lugar. */
  dnsPendente: z.array(RegistroDnsSchema).default([]),
  /** Problemas que o Hosting relata (texto, como vem). */
  problemas: z.array(z.string()).default([]),
  criadoEm: z.coerce.date(),
  verificadoEm: z.coerce.date().nullable().default(null),
  ativoDesde: z.coerce.date().nullable().default(null),
});
export type DominioPersonalizado = z.infer<typeof DominioPersonalizadoSchema>;

/**
 * Domínio de memorial (`memorial.marca.com.br`, 15/10/2026): serve o memorial
 * das experiências especiais da marca — a raiz leva ao memorial no ar — e
 * nunca é a porta dela. Convite, e-mail e link da marca seguem no outro
 * domínio ativo (ou no subdomínio da Eva).
 */
export function ehDominioDeMemorial(hostname: string | null | undefined): boolean {
  return !!hostname && hostname.trim().toLowerCase().startsWith('memorial.');
}

/** O domínio ATIVO que é a porta da marca — o de memorial não conta. */
export function dominioDaPorta(dominios: ReadonlyArray<{ hostname: string; status: string }> | null | undefined): string | null {
  return (dominios ?? []).find((d) => d.status === 'ATIVO' && !ehDominioDeMemorial(d.hostname))?.hostname ?? null;
}

// --- Home Screen Layout (Page Builder) ---

/**
 * Card individual dentro de um CUSTOM_CAROUSEL.
 */
const CarouselCardSchema = z.object({
  id: z.string(),
  imageUrl: z.string().url().or(z.literal('')),
  description: z.string(),
  buttonLabel: z.string(),
  buttonAction: ButtonActionSchema,
});

/**
 * Origem do conteúdo num carrossel de vídeos.
 *
 * - `ALL`      — tudo que o endpoint devolve (comportamento anterior)
 * - `EVA`      — só os globais da Eva (sem `partnerId`)
 * - `PARTNER`  — só os do próprio parceiro
 * - `IMPORTED` — só os importados de outro parceiro
 *
 * Um curso importado carrega o `partnerId` do parceiro de ORIGEM, e é isso que
 * separa `PARTNER` de `IMPORTED`.
 */
const VideoOwnerEnum = z.enum(['ALL', 'EVA', 'PARTNER', 'IMPORTED']);

/**
 * Discriminated union de todos os tipos de componente que podem ser
 * adicionados ao builder da home.
 */
const HomeComponentSchema = z.discriminatedUnion('type', [
  z.object({
    id: z.string(),
    type: z.literal('CTA_CARD'),
    icon: z.string(),                // key do TablerIcons set (vazio = sem ícone)
    title: z.string(),
    description: z.string(),
    buttonLabel: z.string(),
    buttonAction: ButtonActionSchema,
    /** Cores opcionais — usam defaults (cor primária do parceiro) quando ausentes. */
    iconBackgroundColor: z.string().optional(),
    iconColor: z.string().optional(),
    buttonBackgroundColor: z.string().optional(),
    buttonLabelColor: z.string().optional(),
    /**
     * Banner de 2026 (A116): a cor de base do degradê — ausente, o destaque da
     * marca. O ícone e as cores dele (`icon`, `iconBackgroundColor`,
     * `iconColor`) não aparecem mais no banner; ficam no schema para o dado
     * antigo continuar válido. `buttonBackgroundColor` é o círculo do botão.
     */
    backgroundColor: z.string().optional(),
    /**
     * Estilo (A122). `DESTAQUE` (ausente) é o banner da A116: degradê do
     * destaque, texto branco, botão redondo. `CLARO` é o da tela de
     * Conteúdos: degradê rosa-claro ao pêssego, a ilustração num círculo
     * branco (`iconImageUrl`), título escuro e o texto de apoio menor — o
     * banner inteiro é o botão.
     */
    estilo: z.enum(['DESTAQUE', 'CLARO']).optional(),
    iconImageUrl: z.string().url().or(z.literal('')).optional(),
  }),
  z.object({
    id: z.string(),
    type: z.literal('MESSAGE_OF_DAY'),
  }),
  z.object({
    id: z.string(),
    type: z.literal('SECTION_TITLE'),
    text: z.string(),
    /** Link à direita do título ("Ver todos →", A116) — opcional, com a ação. */
    linkLabel: z.string().optional(),
    linkAction: ButtonActionSchema.optional(),
  }),
  /**
   * --- Builder de cuidado -------------------------------------------------
   * Substituíram um protótipo de conteúdo fixo. Estes
   * são alimentados pelo motor (`/api/care/items/pending` e `/api/care/state`),
   * então o que aparece depende da jornada, da semana gestacional e do
   * prontuário de cada usuária.
   */
  z.object({
    id: z.string(),
    /**
     * Só checkpoints. O motor devolve os dois tipos na mesma lista, mas o card
     * daqui abre o wizard — e lembrete nunca é fluxo. Ele tem `CARE_REMINDERS`
     * (aviso fino) e a lista própria em `/care-reminders`.
     */
    /*
     * Sem título próprio: já existe `SECTION_TITLE` como componente. Ter os
     * dois dava duas formas de fazer a mesma coisa, e quem monta a tela teria
     * de lembrar qual usar — e de não usar as duas juntas.
     */
    type: z.literal('CARE_PENDING_ITEMS'),
  }),
  z.object({
    id: z.string(),
    type: z.literal('CARE_JOURNEY_HERO'),
    /**
     * Sem noção de "concluído" por fase: a revisão clínica removeu isso por
     * gerar sensação de atraso. Mostra onde a usuária está, não o que ficou
     * para trás.
     */
    showTrimesterProgress: z.boolean().default(true),
  }),
  z.object({
    id: z.string(),
    /** Sem título próprio — ver `CARE_PENDING_ITEMS`. */
    type: z.literal('CARE_ACTIVE_TOOLS'),
  }),
  z.object({
    id: z.string(),
    /**
     * Aviso fino ("Você tem 3 lembretes ›"), que leva à tela de lembretes —
     * é lá que os cards vivem e são respondidos. Sem configuração: o texto é a
     * contagem, e a contagem vem do motor.
     */
    type: z.literal('CARE_REMINDERS'),
  }),
  z.object({
    id: z.string(),
    type: z.literal('CONTENT_FOR_YOU'),
  }),
  z.object({
    id: z.string(),
    type: z.literal('SHOWCASE'),
    logoUrl: z.string().url().or(z.literal('')),
    title: z.string(),
    description: z.string(),
    /** Vazio = o degradê do tema (A116); com cor, a vitrine de antes (texto branco). */
    backgroundColor: z.string(),
    /** Cores opcionais — usam defaults brancos quando ausentes. */
    titleColor: z.string().optional(),
    descriptionColor: z.string().optional(),
    /**
     * Banner principal de 2026 (A116): o sobretítulo ("Onda de luz"), a
     * ilustração à direita e a ação do botão redondo. Ausentes, a vitrine de
     * antes — sem ação, só apresentação.
     */
    eyebrow: z.string().optional(),
    imageUrl: z.string().url().or(z.literal('')).optional(),
    buttonAction: ButtonActionSchema.optional(),
  }),
  /**
   * "Próximos eventos" (A124): a agenda da semana — os dias, o destaque nos
   * que têm evento — e os cards dos eventos da marca, com a inscrição, e
   * "Ver agenda completa". Lê o cadastro de eventos, não guarda evento. Some
   * sem evento à frente; título próprio pelo mesmo motivo do
   * `CONTINUE_WATCHING`.
   */
  z.object({
    id: z.string(),
    type: z.literal('EVENT_AGENDA'),
    /** Ausente = "Próximos eventos". */
    title: z.string().optional(),
  }),
  /**
   * Filtro por tipo da tela de Conteúdos (A123): "Todos", "Cursos",
   * "Revista", "Podcast"… — os chips saem dos tipos que o próprio layout tem,
   * e escolher um esconde os blocos dos outros tipos. Sem configuração.
   */
  z.object({
    id: z.string(),
    type: z.literal('CONTENT_TYPE_FILTER'),
  }),
  /**
   * Card da jornada (A118): a bonequinha da jornada da usuária, o nome da
   * jornada, uma frase e o convite para a troca de jornada. Os textos padrão
   * estão em `card-da-jornada.ts`; a marca troca só a frase, por jornada
   * (chave = fase da maternidade, ou `ACOMPANHANTE`). Frase vazia = a padrão.
   */
  z.object({
    id: z.string(),
    type: z.literal('JOURNEY_CARD'),
    frases: z.record(z.string(), z.string()).optional(),
  }),
  /**
   * Card que divulga um evento para a paciente se inscrever (A142, 11/10/2026).
   * O evento vem do cadastro da marca (A124): o fixado em `eventoId` ou, sem
   * ele, o próximo da jornada dela em que ainda não se inscreveu. "Inscreva-se"
   * inscreve, e o evento passa a aparecer na agenda dela.
   *
   * Legado (A117): antes o evento era escrito no próprio layout — `title`,
   * `startsAt`, `endsAt`, `url`, `eyebrow` — e o botão só abria a sala. O
   * layout salvo assim continua valendo (o app desenha o card antigo) até a
   * marca escolher um evento do cadastro no builder, que limpa esses campos.
   * Datas em ISO 8601 com fuso, como texto.
   */
  z.object({
    id: z.string(),
    type: z.literal('EVENT_CARD'),
    /** Um evento do cadastro, fixado pela marca. Ausente = o próximo automático. */
    eventoId: z.string().optional(),
    /** Ausente = "Inscreva-se". */
    buttonLabel: z.string().optional(),
    /** Legado (A117): o tipo do evento escrito no layout. */
    eyebrow: z.string().optional(),
    /** Legado (A117). */
    title: z.string().optional(),
    /** Legado (A117). */
    startsAt: z.string().datetime({ offset: true }).optional(),
    /** Legado (A117). */
    endsAt: z.string().datetime({ offset: true }).optional(),
    /** Legado (A117): o link da sala. */
    url: z.string().url().optional(),
  }),
  /**
   * "Continuar assistindo" (A117): os conteúdos que a usuária começou e não
   * terminou, com o progresso, e o botão para todos os conteúdos. Com título
   * próprio — ao contrário dos blocos de cuidado — porque o bloco some
   * quando não há nada começado, e um `SECTION_TITLE` solto ficaria órfão.
   */
  z.object({
    id: z.string(),
    type: z.literal('CONTINUE_WATCHING'),
    /** Ausente = "Continuar assistindo". */
    title: z.string().optional(),
  }),
  /**
   * "Minhas ferramentas" (A117): até quatro ferramentas da jornada — as
   * ativas primeiro, completadas com as disponíveis —, e "Ver todas" para o
   * catálogo. Some sem a funcionalidade de jornadas. Título próprio pelo
   * mesmo motivo do `CONTINUE_WATCHING`.
   */
  z.object({
    id: z.string(),
    type: z.literal('MY_TOOLS'),
    /** Ausente = "Minhas ferramentas". */
    title: z.string().optional(),
    /**
     * Atalhos que não são ferramenta de cuidado, no fim da fila (A126). Hoje
     * só a Comunidade, do desenho de perdi meu bebê; cada um ocupa um dos quatro
     * lugares e só aparece se a marca tem a aba.
     */
    atalhos: z.array(z.enum(['COMUNIDADE'])).optional(),
    /**
     * Aba Cuidado de 2026 (A128): o link do cabeçalho vira "Adicionar novas"
     * com o "+", em vez de "Ver todas" — o mesmo destino, o catálogo.
     */
    linkAdicionar: z.boolean().optional(),
    /**
     * @deprecated Desde 10/10/2026 cada ferramenta aparece sempre na própria
     * cor, em toda tela; o app e o painel ignoram o campo. Continua aceito
     * porque há layouts gravados com ele (A128).
     */
    coresDasFerramentas: z.boolean().optional(),
  }),
  /**
   * Card do checkpoint (A128), o topo da aba Cuidado de 2026: "Olá, Mariana",
   * o nome do primeiro checkpoint pendente em letra grande, a chamada e a
   * seta que pula. Tocar abre o checkpoint. Sem checkpoint pendente, some
   * (decisão do Fábio, 10/10/2026).
   */
  z.object({
    id: z.string(),
    type: z.literal('CARE_CHECKPOINT_CARD'),
    /** Ausente = "Vamos começar o seu checkpoint?". */
    chamada: z.string().max(80).optional(),
  }),
  /**
   * "Meus acompanhamentos" (A130, fase 3): as ferramentas ativas que medem
   * alguma coisa — pressão, glicemia, peso, ciclo, movimentos… —, com a tag
   * do último registro e o medidor em anel. Sem nenhuma, some.
   */
  z.object({
    id: z.string(),
    type: z.literal('CARE_TRACKING'),
    /** Ausente = "Meus acompanhamentos". */
    title: z.string().optional(),
  }),
  /**
   * "Próximo checkpoint" (A128, fase 2): o checkpoint que vem depois do card
   * do topo. Se já está aberto, com "Iniciar agora"; se ainda vai abrir, só
   * a data — não se responde antes da janela (decisão do Fábio, 10/10/2026).
   * Sem nenhum dos dois, some.
   */
  z.object({
    id: z.string(),
    type: z.literal('CARE_NEXT_CHECKPOINT'),
  }),
  /**
   * "Meu plano de cuidado" (A128, fase 2): o que a jornada pediu para ela
   * fazer — os lembretes (exames, consultas) e as ferramentas que os
   * checkpoints recomendaram —, cada um com a tag do estado. Vazio, some.
   */
  z.object({
    id: z.string(),
    type: z.literal('CARE_PLAN'),
    /** Ausente = "Meu plano de cuidado". */
    title: z.string().optional(),
  }),
  /**
   * "Minhas consultas" (A128): a próxima consulta agendada, com a foto, o
   * nome e a ocupação do profissional e o dia e a hora. Sem consulta à
   * frente, some.
   */
  z.object({
    id: z.string(),
    type: z.literal('MY_APPOINTMENTS'),
    /** Ausente = "Minhas consultas". */
    title: z.string().optional(),
  }),
  /**
   * Botão em pílula na largura da tela (A128), como o "Fale com um
   * especialista" da aba Cuidado. O ícone segue a ação (conversa para o
   * chat, formulário, link…).
   */
  z.object({
    id: z.string(),
    type: z.literal('ACTION_BUTTON'),
    label: z.string().min(1).max(40),
    buttonAction: ButtonActionSchema,
  }),
  z.object({
    id: z.string(),
    type: z.literal('COMMUNITY_POST'),
  }),
  z.object({
    id: z.string(),
    type: z.literal('PARTNER_FEED_POST'),
  }),
  z.object({
    id: z.string(),
    type: z.literal('CUSTOM_CAROUSEL'),
    cards: z.array(CarouselCardSchema).min(1),
    /** Cores aplicadas a todos os cards do carrossel (opcional, com defaults). */
    cardBackgroundColor: z.string().optional(),
    cardDescriptionColor: z.string().optional(),
    buttonBackgroundColor: z.string().optional(),
    buttonLabelColor: z.string().optional(),
  }),

  // --- Componentes da tela Conteúdos ---

  /**
   * Carrossel horizontal de vídeos filtrado por subtipo.
   * Ícone do subtipo + título + autor + cover com degradê + logo opcional.
   */
  z.object({
    id: z.string(),
    type: z.literal('VIDEO_CAROUSEL'),
    videoSubtype: VideoSubtypeEnum,
    /**
     * Filtra o carrossel pela origem do conteúdo. O endpoint devolve as três
     * origens fundidas (próprios do parceiro + globais da Eva + importados de
     * outro parceiro), e a tela antiga fatiava esse resultado em duas seções:
     * "Exclusivos {Parceiro}" e "Originais Eva". Sem este campo o builder só
     * conseguia reproduzir a lista inteira, sem rótulo de origem.
     *
     * Nasce com `.default('ALL')` para não alterar os layouts já publicados.
     */
    videoOwner: VideoOwnerEnum.default('ALL'),
    iconBackgroundColor: z.string().optional(),
    iconColor: z.string().optional(),
    gradientColor: z.string().optional(),
    logoUrl: z.string().url().or(z.literal('')).optional(),
  }),

  /**
   * Lista vertical de cards de vídeo filtrada por subtipo.
   * Ícone do subtipo à esquerda + título + descrição + chevron.
   */
  z.object({
    id: z.string(),
    type: z.literal('VIDEO_LIST'),
    videoSubtype: VideoSubtypeEnum,
    iconBackgroundColor: z.string().optional(),
    iconColor: z.string().optional(),
    cardBackgroundColor: z.string().optional(),
    titleColor: z.string().optional(),
    descriptionColor: z.string().optional(),
    /**
     * Quantos mostrar, no máximo (10/10/2026) — "Podcast e lives" mostra um de
     * cada, e o "Ver todos" do título leva à lista completa. Ausente = todos.
     */
    limite: z.number().int().min(1).max(50).optional(),
  }),

  /**
   * Carrossel horizontal de revistas (segue a audiência da tela).
   * Sem config: o renderer escolhe o endpoint baseado na fase atual
   * (do usuário ou do JOURNEY_PICKER).
   */
  z.object({
    id: z.string(),
    type: z.literal('MAGAZINE_CAROUSEL'),
  }),

  /**
   * Seletor horizontal de jornada — filtra todos os carrosseis/listas
   * abaixo na mesma tela. Inicia na fase atual do usuário.
   */
  z.object({
    id: z.string(),
    type: z.literal('JOURNEY_PICKER'),
    selectedBackgroundColor: z.string().optional(),
    selectedIconColor: z.string().optional(),
    unselectedBackgroundColor: z.string().optional(),
    unselectedIconColor: z.string().optional(),
  }),

  /**
   * Carrossel horizontal de trilhas — equivalente à seção "Trilhas Disponíveis"
   * da tela de Conteúdos anterior ao builder. Sem ele não havia como colocar
   * trilha em nenhuma das duas telas.
   */
  z.object({
    id: z.string(),
    type: z.literal('TRAIL_CAROUSEL'),
    /**
     * `AVAILABLE` reproduz a seção antiga (só trilhas em que a usuária ainda
     * não se inscreveu); `ENROLLED`, as que ela já cursa.
     */
    trailFilter: z.enum(['ALL', 'AVAILABLE', 'ENROLLED']).default('ALL'),
    iconBackgroundColor: z.string().optional(),
    iconColor: z.string().optional(),
    gradientColor: z.string().optional(),
    logoUrl: z.string().url().or(z.literal('')).optional(),
  }),

  /**
   * Progresso das trilhas em que a usuária já está inscrita — uma seção por
   * trilha, com barra de progresso, contagem de cursos obrigatórios, selo de
   * certificado e o carrossel dos cursos da trilha.
   *
   * Equivale ao bloco que a tela de Conteúdos renderizava no topo antes do
   * builder. Distinto do TRAIL_CAROUSEL, que lista trilhas como cards.
   */
  z.object({
    id: z.string(),
    type: z.literal('TRAIL_PROGRESS'),
    /** Trilha concluída continua na lista (é onde fica o certificado). */
    includeCompleted: z.boolean().default(true),
    progressColor: z.string().optional(),
    completedColor: z.string().optional(),
  }),
]);

/**
 * Configuração de layout da tela inicial — lista ordenada de componentes
 * configurados pelo parceiro.
 */
const HomeScreenLayoutSchema = z.object({
  version: z.literal(1),
  components: z.array(HomeComponentSchema),
  updatedAt: z.coerce.date().optional(),
});

/**
 * Preferências globais de UI do app mobile do parceiro.
 * Distinto de `HomeScreenLayout` (que é por tela) — aqui vão ajustes que
 * afetam o app inteiro (tab bar, header, etc).
 */
/**
 * URLs opcionais para ícones customizados de cada fase da maternidade.
 * Quando ausente para uma fase, o mobile usa o Ionicon padrão.
 * Aplicado em: modal "Fase da Maternidade" e componente JOURNEY_PICKER.
 * Formato esperado: SVG (mas qualquer URL de imagem que o SvgUri/Image suporte funciona).
 */
const MaternityPhaseIconsSchema = z.object({
  TENTANTE: z.string().url().optional(),
  GESTANTE: z.string().url().optional(),
  PUERPERA: z.string().url().optional(),
  MAE_DE_ANJO: z.string().url().optional(),
  ADOTANTE: z.string().url().optional(),
  MAE: z.string().url().optional(),
});

/**
 * Campos que a marca pode pedir nos dados pessoais do onboarding (A062,
 * fase C). A marca escolhe se aparecem e, desde 06/10/2026, se são
 * obrigatórios (`camposObrigatorios`). A Eva Maternidade pede os três, para
 * comunicação e perfil regional.
 *
 * `empresa` (A097): a empresa onde a pessoa diz trabalhar — dado DECLARADO,
 * guardado no perfil (`empresaOndeTrabalha`). Não cria empresa, não vincula,
 * não dá acesso nem benefício: isso continua só pelo link, pelo domínio ou
 * pelo convite da empresa (A062).
 */
export const CampoExtraDoOnboardingEnum = z.enum(['telefone', 'cidade', 'estado', 'empresa']);
export type CampoExtraDoOnboarding = z.infer<typeof CampoExtraDoOnboardingEnum>;

/**
 * Estado e cidade andam juntos (A100): a cidade é escolhida dentro do estado,
 * então só existe com o estado, vem logo depois dele e, se for obrigatória,
 * o estado também é. Arruma qualquer lista — inclusive as gravadas antes
 * desta regra, como a da Eva Maternidade em QA, com cidade antes de estado —
 * sem migração: quem lê ou grava passa por aqui.
 */
export function arrumarCamposDoOnboarding(
  extras: readonly CampoExtraDoOnboarding[] | null | undefined,
  obrigatorios: readonly CampoExtraDoOnboarding[] | null | undefined = []
): { campos: CampoExtraDoOnboarding[]; obrigatorios: CampoExtraDoOnboarding[] } {
  let campos = [...new Set(extras ?? [])];
  campos = campos.filter((c) => c !== 'cidade');
  if ((extras ?? []).includes('cidade') && campos.includes('estado')) campos.splice(campos.indexOf('estado') + 1, 0, 'cidade');
  const exigidos = [...new Set(obrigatorios ?? [])].filter((c) => campos.includes(c));
  if (exigidos.includes('cidade') && !exigidos.includes('estado')) exigidos.push('estado');
  return { campos, obrigatorios: exigidos };
}

/** Momentos com vídeo próprio na home, depois do onboarding. */
export const MomentoComVideoEnum = z.enum(['GESTANTE', 'PUERPERA', 'TENTANTE', 'MAE_DE_ANJO', 'ADOTANTE', 'MAE', 'PARCEIRO']);

/**
 * Um vídeo que a própria marca enviou (05/10/2026): sobe direto ao bucket por
 * URL assinada e passa pelo transcoder, como o vídeo do feed. O app toca pelo
 * stream assinado do `filename`; só entra no onboarding quando `READY`.
 */
export const VideoDaMarcaSchema = z.object({
  filename: z.string().min(1),
  videoId: z.string().min(1),
  transcodingStatus: TranscodingStatusEnum,
  jobId: z.string().optional(),
  transcodedUrls: z.record(z.string()).optional(),
  transcodingError: z.string().optional(),
  durationSeconds: z.number().optional(),
  /**
   * Como o vídeo aparece na tela — já com a rotação do metadado (13/10/2026;
   * antes era a dos pixels codificados, e o vídeo de celular gravado em pé
   * saía em moldura deitada). Ausente quando não deu para ler.
   */
  orientacao: z.enum(['VERTICAL', 'HORIZONTAL']).optional(),
  /** Nome do arquivo como a marca enviou, para a tela reconhecer o vídeo. */
  nomeOriginal: z.string().optional(),
  enviadoEm: z.coerce.date().optional(),
});
export type VideoDaMarca = z.infer<typeof VideoDaMarcaSchema>;

/**
 * Teto do título do vídeo da jornada (A119): no modal ele vai em Poppins 30
 * centralizado, e 40 caracteres cabem em até três linhas na largura de um
 * celular. Mais que isso empurra o vídeo para fora da tela.
 */
export const TITULO_DO_VIDEO_DA_JORNADA_MAX = 40;

/**
 * O onboarding da marca (A062, fase C): o vídeo de boas-vindas, que vem
 * ANTES das perguntas, e o vídeo de cada jornada, que aparece na home
 * depois. Sem vídeo, a etapa some.
 *
 * Quem configura é a própria marca, em Configurações › Onboarding, enviando o
 * arquivo (`videoBoasVindas`, `videosDaJornada`). As URLs da fase C
 * (`videoBoasVindasUrl`, `videosPorMomento`) ainda são lidas, como reserva,
 * mas nenhuma tela grava mais nelas. A marca corporativa nasce com o
 * onboarding da mãe — o vídeo da Mari — e pode trocar.
 */
export const OnboardingDaMarcaSchema = z.object({
  videoBoasVindas: VideoDaMarcaSchema.nullable().optional(),
  videosDaJornada: z.record(MomentoComVideoEnum, VideoDaMarcaSchema).optional(),
  /**
   * O título acima do vídeo da jornada ("Você não está sozinha!"), opcional,
   * por jornada (A119). Fora do objeto do vídeo de propósito: o webhook do
   * transcoder regrava o vídeo, e trocar o arquivo não deve apagar o título.
   */
  titulosDaJornada: z.record(MomentoComVideoEnum, z.string().trim().max(TITULO_DO_VIDEO_DA_JORNADA_MAX)).optional(),
  videoBoasVindasUrl: z.string().url().nullable().optional(),
  videosPorMomento: z.record(MomentoComVideoEnum, z.string().url()).optional(),
  camposExtras: z.array(CampoExtraDoOnboardingEnum).default([]),
  /**
   * Dos campos extras, os que a pessoa precisa preencher para seguir
   * (06/10/2026). A marca escolhe; os demais continuam opcionais.
   */
  camposObrigatorios: z.array(CampoExtraDoOnboardingEnum).default([]),
});
export type OnboardingDaMarca = z.infer<typeof OnboardingDaMarcaSchema>;

const AppUiPreferencesSchema = z.object({
  /** Mostrar rótulos abaixo dos ícones na tab bar inferior. Default true. */
  showTabLabels: z.boolean().default(true),
  /** Ícones customizados por fase. Cada um é opcional — fallback é Ionicon. */
  maternityPhaseIcons: MaternityPhaseIconsSchema.optional(),
  /**
   * A página de entrar orienta só o cadastro de paciente (A039).
   *
   * Quando a marca tem rede de profissionais (`PARTNER_PROVIDERS`), a página
   * de entrar mostra os dois caminhos de "criar conta": paciente, e
   * profissional (que explica que o acesso nasce de um convite). Esta chave
   * desliga o segundo: a marca tem rede, mas prefere que a porta fale só com
   * mães e figuras paternas. Desligada por padrão.
   */
  loginOrientaSoPacientes: z.boolean().default(false),
});

// --- Main Partner Schema ---

/**
 * Define o schema principal e completo para a entidade Partner.
 */
const PartnerSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: PartnerType,
  status: PartnerStatus,
  slug: z.string()
    .min(3, 'Slug deve ter no mínimo 3 caracteres')
    .regex(/^[a-z0-9-]+$/, 'Slug deve conter apenas letras minúsculas, números e hífens')
    .refine(val => !['login', 'dashboard', 'users', 'chat', 'partners', 'providers', 'families', 'team', 'content', 'settings', 'api', 'auth', 'health', 'public', 'assets', 'static'].includes(val), 'Este slug é uma palavra reservada do sistema')
    .optional()
    .nullable(),
  document: z.string(),
  branding: BrandingSchema.optional(),

  // [Feature Gating]
  /**
   * Parceiro criado pela varredura agêntica, não um cliente.
   *
   * Existe porque espelho é parceiro comum do ponto de vista do banco: qualquer
   * agregação que atravesse `partners` o incluiria, e junto o dado que a
   * varredura escreve dentro dele. Em produção seriam dois numa base de 16 —
   * o bastante para deslocar percentual de forma perceptível.
   *
   * `.default(false)` de propósito: os parceiros existentes passam a ler como
   * `false` sem nenhuma escrita, que é o padrão de expand do AGENTS.md.
   *
   * NÃO usar `status` para isto — ele é operacional (`active`/`inactive`), e o
   * espelho precisa estar `active` justamente para ser exercitado.
   *
   * Ver docs/varredura-backlog/2026-08-espelhos-nas-metricas.md
   */
  isTeste: z.boolean().default(false),

  /**
   * Funcionalidades habilitadas pelos mantenedores.
   *
   * O catálogo canônico mora em `brand-features.ts`. Antes era
   * `z.array(z.string())`, e a lista fechada existia só num `<MultiSelect>` do
   * admin — o que deixava um PATCH gravar qualquer coisa (foi assim que
   * `PROVIDER_CHAT`, que ninguém lê, entrou pelo seeder).
   *
   * Valor desconhecido é descartado na leitura em vez de invalidar o
   * documento: `findMany` lança no primeiro documento inválido, então rejeitar
   * transformaria um dado sujo antigo em queda da listagem de parceiros.
   */
  enabledFeatures: brandFeaturesField(),

  /**
   * O que esta marca mudou na matriz de chat (A030). **Legado** desde a A146:
   * os canais substituíram a matriz, e ninguém lê nem grava o campo. Fica
   * aceito na leitura porque há marcas com ele gravado.
   */
  // `nullable` de propósito: um `null` gravado aqui — por script, por um
  // "limpar" no admin — derrubava a marca inteira na leitura, e com ela o
  // login de quem entra por essa porta. Aconteceu no emulador em 11/09/2026.
  chatPolicy: ChatPolicySchema.nullable().optional(),

  /**
   * A marca pode dar identidade visual própria às empresas da carteira dela.
   *
   * A decisão é da marca, não da empresa — é o que o plano chama de
   * "configurado para a marca na contratação". Nasce `false` para que os
   * parceiros existentes leiam sem nenhuma escrita.
   */
  allowsCompanyBranding: z.boolean().default(false),

  contacts: z.array(PartnerContactSchema).optional(),

  // Preferências de conteúdo (Vídeos Eva, Comentários, etc.)
  contentPreferences: ContentPreferencesWithLegacyAliasesSchema.optional(),

  // Configuração de integração com a plataforma Cativa (comunidade)
  cativaConfig: z.object({
    customerSlug: z.string(),
    customerId: z.string(),
    badges: z.array(z.object({
      id: z.string(),
      name: z.string(),
      imageUrl: z.string(),
    })).optional().default([]),
    /**
     * Grupos da comunidade que a paciente vê em cada momento (A152): chave é
     * a fase (`MaternityPhase`: TENTANTE, GESTANTE, PUERPERA, MAE —
     * pós-licença —, MAE_DE_ANJO, ADOTANTE), valor é a lista de ids de grupo
     * da Cativa. A Cativa não muda: a Eva filtra o feed e publica no primeiro
     * grupo do momento. Momento sem grupo vê só os comuns.
     */
    gruposPorMomento: z.record(z.string(), z.array(z.string())).optional(),
    /** Grupos que todos os momentos veem ("Sua rede de apoio", conteúdos). */
    gruposComuns: z.array(z.string()).optional(),
    /**
     * A badge que a Eva dá na comunidade a cada momento (A153): fase → id da
     * badge. Só a badge que a Eva deu é trocada quando o momento muda; as que
     * a Cativa deu por outros caminhos ficam.
     */
    badgePorMomento: z.record(z.string(), z.string()).optional(),
  }).optional(),

  // Configuração de Barreiras (Paywall e Formulários de Adesão)
  barriers: BarrierConfigSchema.optional(),

  /**
   * Bloqueios da marca (14/10/2026): o que ela restringe para as pacientes,
   * em regras — `bloqueio.schema.ts`. Opcional: marca sem regras não bloqueia nada.
   */
  bloqueios: z.array(RegraDeBloqueioSchema).optional(),

  // Layout customizado da tela inicial (Page Builder)
  homeScreenLayout: HomeScreenLayoutSchema.optional(),

  // Layout customizado da tela de Conteúdos (Page Builder)
  contentScreenLayout: HomeScreenLayoutSchema.optional(),

  // Layout customizado da tela de Cuidado (Page Builder). É a aba do coração:
  // os componentes da jornada saíram da home e moram aqui.
  careScreenLayout: HomeScreenLayoutSchema.optional(),

  // Preferências globais de UI do app mobile (tab bar, etc)
  appUiPreferences: AppUiPreferencesSchema.optional(),

  /** Vídeos e campos extras do onboarding (A062, fase C). */
  onboarding: OnboardingDaMarcaSchema.optional(),

  /**
   * Domínios de e-mail da marca, para o link da marca inteira no modo
   * validado (A062, fase D). É o caso da marca corporativa: a empresa é a
   * marca. Mesmas regras de `companies.dominios`; editados pela
   * administradora da marca.
   */
  dominios: dominiosField().optional(),

  /**
   * Teto de vidas da marca (A062, fase D): quantas pacientes ativas ela pode
   * ter, contando colaboradoras e dependentes. É o que foi vendido, então
   * quem define é o mantenedor, nunca a administradora da marca corporativa.
   * Ausente ou `null` = sem teto.
   */
  vagas: z.number().int().positive().nullable().optional(),

  /**
   * Cadastro só com link ou convite (A062, fase D): a porta pública da marca
   * não aceita cadastro avulso. Sem isso, na marca corporativa, qualquer
   * pessoa se cadastraria pela porta e a validação por domínio do link não
   * protegeria nada. Do mantenedor.
   */
  cadastroSoComLink: z.boolean().optional(),

  /**
   * A marca corporativa nasceu do modelo (A062, fase D): de qual marca mãe e
   * quando. Serve para saber quem atende e de onde vieram identidade,
   * telas e concessões — depois disso, cada uma segue a própria vida.
   */
  origemDoModelo: z
    .object({ marcaMaeId: z.string(), em: z.coerce.date() })
    .optional(),

  /**
   * Curadoria das jornadas recebidas por concessão (fase 4): o que a marca
   * escolheu não mostrar às pacientes dela, por id de item. A concessão diz
   * o que chega; isto diz o que ela não quer.
   */
  jornadasRecebidas: z.object({
    ocultas: z.array(z.string()).default([]),
  }).optional(),

  /**
   * Domínios personalizados da marca (`app.marca.com.br` no lugar de
   * `marca.app.evasaude.com.br`). A mantenedora cadastra; o Firebase Hosting
   * é quem verifica posse, DNS e certificado, e o estado aqui espelha o dele.
   * Só domínio `ATIVO` monta endereço (links, e-mails): antes do certificado
   * o host já responde, mas com erro de TLS.
   */
  dominiosPersonalizados: z.array(DominioPersonalizadoSchema).default([]),

  // Configuração de agendamento (feature CALENDLY_SCHEDULING).
  // Apenas defaults não-sensíveis: os tokens OAuth e a signing key do webhook
  // ficam na coleção `calendlyConnections` — ver calendly-connection.schema.ts.
  schedulingConfig: PartnerSchedulingConfigSchema.optional(),

  // Campos de auditoria e exclusão lógica.
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable().default(null),
});


// --- Inferred TypeScript Types ---

type Partner = z.infer<typeof PartnerSchema>;
type PartnerType = z.infer<typeof PartnerType>;
type PartnerStatus = z.infer<typeof PartnerStatus>;
type Branding = z.infer<typeof BrandingSchema>;
type PartnerContact = z.infer<typeof PartnerContactSchema>;
type ContentPreferences = z.infer<typeof ContentPreferencesSchema>;
type CarouselCard = z.infer<typeof CarouselCardSchema>;
type HomeComponent = z.infer<typeof HomeComponentSchema>;
type HomeScreenLayout = z.infer<typeof HomeScreenLayoutSchema>;
type AppUiPreferences = z.infer<typeof AppUiPreferencesSchema>;


// --- Public Exports ---

export {
  PartnerSchema,
  PartnerType,
  PartnerStatus,
  BrandingSchema,
  PartnerContactSchema,
  ContentPreferencesSchema,
  CarouselCardSchema,
  HomeComponentSchema,
  HomeScreenLayoutSchema,
  AppUiPreferencesSchema,
  MaternityPhaseEnum,
};
export type {
  Partner,
  Branding,
  PartnerContact,
  ContentPreferences,
  CarouselCard,
  HomeComponent,
  HomeScreenLayout,
  AppUiPreferences,
  MaternityPhase,
};
